import {
  stripHtml,
  toNumber,
  currencyEligible,
  currencyMarkedColumnIds,
  currencyToggleActs,
  numericTotalColumnIds,
  showDollarNumeric,
  detectNumberFormat,
  summableColumns,
  columnAggregate,
  groupsThousands,
} from './reportGridUtils'
import { exportCell, exportText } from './exportCell'

/**
 * The grouped grid's EXPORT VIEW as a pure function (spec 127 §4.1, slice S3).
 *
 * MOVED, unchanged in output, out of `ReportGroupedGrid.jsx`'s export-view effect
 * so a consumer that has no mounted grid — the queue drawer printing a stored
 * statement job — builds exactly the view the page's grid would publish. The grid
 * now calls this too; there is one builder. Every field that existed before the
 * move is pinned by `__tests__/groupedExportView.test.js` against views recorded
 * BEFORE it, on 28 cases (I15).
 *
 * Two additive fields:
 *   - `columns[i].percent` — `!!col.percent`. A column must DECLARE it is a
 *     percentage; nothing is detected (the generic engine's percent semantics are
 *     unmeasured and belong to C1).
 *   - `outline` / `outlineAll` — the grouped body in render order, for a PDF that
 *     prints what the screen shows (headings, rows, subtotal lines). See
 *     `buildOutline` below.
 * Spec 153 adds two more: `columns[i].aggregate`, present only where CB declares
 * an AVG / MIN / MAX / COUNT report footer (`nonSumReportAggregate`), and
 * `totalsRecomputed`, whether the grand-total row shows a re-add of the visible
 * leaves (see the field).
 *
 * Nothing here computes money except what the grid already did: the SUM-only
 * filtered subtotal (`footerRecompute`), which is the SAME helper the grid's
 * on-screen footer uses, so the outline can never show a subtotal the screen
 * does not.
 */

// ---- shared primitives (the grid imports these; one definition each) ----------

// Typographic entities a page writes into an HTML-string group HEADER that
// `stripHtml` (reportGridUtils) does not decode — it knows only the markup six
// (&quot; &#39; &nbsp; &lt; &gt; &amp;). Aged A/R's three-level heading joins
// "Client › Attorney › Invoice" with `&rsaquo;` (ARReport.jsx), so every heading of
// the grouped PDF printed the literal "&rsaquo;" while the screen showed "›"
// (review of spec 127). Decoded on the RAW html, before stripHtml: a user's literal
// "&rsaquo;" arrives escaped as "&amp;rsaquo;", which this never matches, so nothing
// is over-decoded. Numeric references decode only at U+00A0 and above — never into
// a character that is markup.
const HEADING_ENTITIES = {
  rsaquo: '›', lsaquo: '‹', raquo: '»', laquo: '«', middot: '·',
  ndash: '–', mdash: '—', hellip: '…', bull: '•',
  lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”',
}
const decodeHeadingEntities = (html) => String(html ?? '')
  .replace(/&([a-z]+);/gi, (m, name) => HEADING_ENTITIES[name.toLowerCase()] ?? m)
  .replace(/&#(?:x([0-9a-f]+)|(\d+));/gi, (m, hex, dec) => {
    const cp = hex ? parseInt(hex, 16) : parseInt(dec, 10)
    return Number.isFinite(cp) && cp >= 0xa0 && cp <= 0x10ffff ? String.fromCodePoint(cp) : m
  })

/** A group header's text as the screen shows it (tags stripped, entities decoded). */
export const groupHeadingText = (html) => stripHtml(decodeHeadingEntities(html))

export const cellById = (cells, id) => cells?.find((c) => String(c.columnId) === String(id))

/** Numeric value of a CB cell for SUM recompute: prefer dataValue, fall back to display. */
export const cellNumber = (cell) => toNumber(cell?.dataValue != null ? cell.dataValue : cell?.displayValue)

/** Recursively collect all leaf (non-group) rows under the given rows. */
export const collectLeaves = (rows) => {
  const out = []
  for (const r of rows || []) {
    if (r.isGroup) out.push(...collectLeaves(r.dataRows))
    else out.push(r)
  }
  return out
}

/**
 * The identity of a group row in the grid's `collapsed` map (CC-49843). Positional
 * (path + level + index), so the renderer and the export view MUST walk the same
 * base list — `rows` when not paging, `visibleTop` when paging (`collapseBase`).
 */
export const groupRowKey = (keyPrefix, level, index) => `${keyPrefix}g-${level}-${index}`

/**
 * CC-50050: a group's subtotal BLOCK. `groupFooterRows` (array of cell arrays) is
 * the multi-line form; everything else keeps its single `groupFooterCells` row.
 */
export const groupFooterLines = (g) => {
  const multi = g?.groupFooterRows
  if (Array.isArray(multi) && multi.length > 0) return multi.filter((r) => (r || []).length > 0)
  return g?.groupFooterCells?.length ? [g.groupFooterCells] : []
}

/**
 * CC-49860: the report footer BLOCK — `reportFooterRows` when a report closes with
 * several total rows, else the single `reportFooterCells` row.
 */
export const reportFooterLines = (report) => {
  const multi = report?.reportFooterRows
  if (Array.isArray(multi) && multi.length > 0) return multi.filter((r) => (r || []).length > 0)
  const cells = report?.reportFooterCells || []
  return cells.length > 0 ? [cells] : []
}

/**
 * SUM-only classification (CC-49382): which columns are safe to re-add. A column
 * is summable when its server GRAND total equals the sum of every leaf, or its
 * declared footer aggregate says SUM (CC-49302).
 */
export const computeSummable = (report, columns) => {
  const grand = new Map()
  for (const c of report?.reportFooterCells || []) {
    const n = toNumber(c.displayValue)
    if (n != null) grand.set(String(c.columnId), n)
  }
  const leaves = collectLeaves(report?.reportData || [])
  const allValues = new Map()
  for (const colId of grand.keys()) {
    const vals = []
    for (const lf of leaves) {
      const n = cellNumber(cellById(lf.dataCells, colId))
      if (n != null) vals.push(n)
    }
    allValues.set(colId, vals)
  }
  const aggregateByCol = new Map((columns || []).map((c) => [String(c.columnId), columnAggregate(c)]))
  return summableColumns(allValues, grand, aggregateByCol)
}

/**
 * Does re-adding EVERY leaf of a scope reproduce the figure CB displayed for it?
 * The per-scope half of the SUM-only guardrail, shared by the grouped footer
 * (`footerRecompute`) and the grouped client export (`exportClientView`), so the
 * screen and the file can never disagree about which totals they re-add.
 *
 * CC-54262 (spec 180): CB DISPLAYS a total rounded to the column's format while
 * every leaf carries the unrounded figure, so a true plain sum sits up to half a
 * unit of the displayed last place away from CB's display. Measured on report
 * 190, All Dates: Non Billable Hours' 121 leaves add to 1,946.67035406 (CB's own
 * footer dataValue, to 5e-13) and CB displays "1,946.7" — 0.0296 apart. The old
 * fixed 0.01 floor called that "not a simple sum" and the grouped footer kept the
 * whole report's 1,946.7 under a column filter, while Billable and Total Hours
 * passed only because 1e-6 of a five-digit total happens to exceed their gap.
 * The half unit is added as a third floor, so this only ever WIDENS the old
 * tolerance — and only on a figure displayed with fewer than two decimals.
 *
 * A displayed half is a TIE CB really prints: one leaf of that payload carries
 * 23.45 and CB shows "23.5", another -0.95 and CB shows "-1.0". In binary floating
 * point those gaps measure 0.05000000000000071, a hair over 0.05, so the half unit
 * carries a 1e-9 relative slack — far below any displayed digit.
 *
 * @param {number} sum            the re-add of every leaf in the scope
 * @param {string} serverDisplay  CB's displayed figure for that scope
 */
export const reAddMatchesServer = (sum, serverDisplay) => {
  const serverNum = toNumber(serverDisplay)
  if (serverNum == null || typeof sum !== 'number' || !Number.isFinite(sum)) return false
  const shownDecimals = stripHtml(String(serverDisplay)).match(/\.(\d+)/)?.[1].length ?? 0
  const halfUnit = 0.5 * 10 ** -shownDecimals + Math.max(1, Math.abs(serverNum)) * 1e-9
  const tol = Math.max(0.01, Math.abs(serverNum) * 1e-6, halfUnit)
  return Math.abs(sum - serverNum) <= tol
}

/**
 * The filtered-subtotal decision (bug #2 / CC-49456), extracted so the screen's
 * footer cell and the export outline take it from ONE place. A column is re-summed
 * over the visible leaves only when it is a true plain sum FOR THIS SCOPE — this
 * scope's server total equals the sum of ALL its own leaves (`reAddMatchesServer`).
 * Otherwise the server value stands, and `kept` says the screen should mark it.
 *
 * @returns {{ safe: boolean, sum: number|null, kept: boolean }}
 */
export const footerRecompute = ({ colId, serverDisplay, visibleLeaves, allLeaves, isFiltered, recompute, summable }) => {
  const serverNum = toNumber(serverDisplay)
  let safe = false
  if (isFiltered && recompute && summable?.has(colId) && serverNum != null) {
    const allSum = allLeaves.reduce((acc, lf) => acc + (cellNumber(cellById(lf.dataCells, colId)) ?? 0), 0)
    safe = reAddMatchesServer(allSum, serverDisplay)
  }
  if (safe) {
    const sum = visibleLeaves.reduce((acc, lf) => acc + (cellNumber(cellById(lf.dataCells, colId)) ?? 0), 0)
    return { safe: true, sum, kept: false }
  }
  return { safe: false, sum: null, kept: !!(isFiltered && recompute && serverNum != null) }
}

// ---- statement mode: tree rows ------------------------------------------------

/** Guard against a malformed parent chain (a cycle would otherwise never end). */
const MAX_CHAIN = 64

/**
 * Index a statement's tree rows (spec 127 §4.1 S4–S7).
 *
 * A leaf row may carry `tree = { id, parentId, depth, kind: 'node'|'close',
 * rolledCells? }`. The PAGE builds `rolledCells` from CB's own rolled figures; this
 * index only decides what is visible and which cells a row shows. It never sums.
 *
 *  - rollable   node ids whose row carries `rolledCells` (the page emits them iff
 *               the node has a close row).
 *  - rolled     the requested ids ∩ rollable — an id from another run, a saved view
 *               or an account that has since lost its children is IGNORED (S4).
 *  - a row is roll-up-hidden when any ancestor on its `parentId` chain is rolled;
 *    a `close` row also when its own id is rolled (S5).
 *  - a rolled node DISPLAYS `rolledCells` instead of `dataCells` (S5).
 *
 * Refuses the whole tree (valid:false — every row renders as an ordinary row) when
 * two node rows or two close rows share an id: a collision would hide the wrong
 * rows. S4's page builder refuses first; this is the grid's own backstop.
 */
export function buildStatementIndex(report, rolledIds) {
  const leaves = collectLeaves(report?.reportData || [])
  const nodes = new Map()
  const closes = new Map()
  const children = new Map()
  let valid = true
  let hasTree = false
  for (const r of leaves) {
    const t = r?.tree
    if (!t || t.id == null) continue
    hasTree = true
    const bucket = t.kind === 'close' ? closes : nodes
    if (bucket.has(t.id)) valid = false
    bucket.set(t.id, r)
    if (t.kind !== 'close' && t.parentId != null) {
      if (!children.has(t.parentId)) children.set(t.parentId, [])
      children.get(t.parentId).push(r)
    }
  }
  if (!valid) {
    return {
      valid: false, hasTree, rollable: new Set(), rolled: new Set(), rolledInOrder: [],
      treeOf: () => null, isRolled: () => false, rollupHidden: () => false,
      displayCells: (row) => row?.dataCells, childrenOf: () => [],
    }
  }
  const rollable = new Set()
  for (const [id, row] of nodes) if (Array.isArray(row.tree.rolledCells)) rollable.add(id)
  const requested = new Set(Array.isArray(rolledIds) || rolledIds instanceof Set ? [...rolledIds] : [])
  const rolled = new Set([...rollable].filter((id) => requested.has(id)))

  const treeOf = (row) => (row?.tree && row.tree.id != null ? row.tree : null)
  const isRolled = (row) => {
    const t = treeOf(row)
    return !!t && t.kind !== 'close' && rolled.has(t.id)
  }
  const ancestorRolled = (parentId) => {
    let p = parentId
    for (let i = 0; p != null && i < MAX_CHAIN; i++) {
      if (rolled.has(p)) return true
      p = nodes.get(p)?.tree?.parentId ?? null
    }
    return false
  }
  const rollupHidden = (row) => {
    const t = treeOf(row)
    if (!t) return false
    if (t.kind === 'close' && rolled.has(t.id)) return true
    const parentId = t.parentId !== undefined ? t.parentId : nodes.get(t.id)?.tree?.parentId
    return ancestorRolled(parentId)
  }
  const displayCells = (row) => (isRolled(row) ? row.tree.rolledCells : row?.dataCells)
  const childrenOf = (id) => children.get(id) || []
  // CB order, so a published / saved rolled set is deterministic.
  const rolledInOrder = leaves.filter((r) => isRolled(r)).map((r) => r.tree.id)
  return { valid: true, hasTree, rollable, rolled, rolledInOrder, treeOf, isRolled, rollupHidden, displayCells, childrenOf, nodes, closes }
}

/**
 * Tree-aware "Hide $0" (spec 127 §4.1 S6). Returns `keep(row)` for TREE rows only:
 *  - a node is kept if its DISPLAYED cells are not all zero (a rolled node shows
 *    its rolledCells), or if any child would be visible under it (bottom-up; a
 *    child counts when it passes `passesOther` and is itself kept);
 *  - a close row is kept iff its node is kept (and it is already hidden by roll-up
 *    when its node is rolled).
 * The per-row rule hid Rent / Travel and Entertainment (own $0.00) while their
 * children stayed (verifier 1, C18b). `passesOther` is the grid's per-row test
 * WITHOUT the zero rule (column filters, inactive, aging), which stay per row.
 *
 * A ROLLED node is judged on its expanded subtree too, not on its rolledCells
 * alone (review of spec 127): a parent whose sub-accounts net to $0.00 is visible
 * expanded — its children are not zero — so rolling it up must not make it, and
 * the toggle the user just pressed, vanish (focus fell to <body>, WCAG 2.4.3, and
 * only "Expand all" could bring the account back). Rolling is a presentation swap,
 * so it never changes whether Hide $0 keeps an account: its children are counted
 * as they would be with it expanded (each sub-parent keeps its OWN rolled state).
 */
export function treeZeroKeep(index, { isZeroCells, passesOther }) {
  const memo = new Map()
  const keepNode = (row, depth = 0) => {
    if (memo.has(row)) return memo.get(row)
    memo.set(row, true) // provisional — a cycle can only be malformed data; do not hide on it
    let keep = !isZeroCells(index.displayCells(row))
    if (!keep && depth < MAX_CHAIN) {
      // A child hidden only because THIS node (or an ancestor) is rolled still
      // counts: it is what the node shows when expanded. When the node itself is
      // shown, no ancestor is rolled, so the only roll-up hiding its children is
      // its own — exactly the state this rule must not depend on.
      keep = index.childrenOf(row.tree.id).some(
        (c) => passesOther(c) && keepNode(c, depth + 1)
      )
    }
    memo.set(row, keep)
    return keep
  }
  return (row) => {
    const t = index.treeOf(row)
    if (!t) return true
    if (t.kind === 'close') {
      const node = index.nodes?.get(t.id)
      return node ? keepNode(node) : !isZeroCells(row.dataCells)
    }
    return keepNode(row)
  }
}

/**
 * Spec 153: the aggregate CB DECLARES for a column's REPORT footer when it is not a
 * plain sum — AVG / MIN / MAX / COUNT — else null. It rides on the export view's
 * column as `aggregate`, so the AI payload can send CB's figure for what it is
 * ("Actual Rate (average, whole report)", report 137) instead of dropping it.
 *
 *  - Report level only. A group-footer declaration says nothing about the report
 *    total: on vladdemo 177 / 194 / 202 / 215 / 291 declare MAX / AVG / COUNT per
 *    group, SUM at report level, and show no report footer for those columns.
 *    Only 137 declares a non-SUM REPORT aggregate (all 119 definitions scanned,
 *    2026-10-01).
 *  - SUM is never published: CB stamps it on columns it never totals (CC-49944,
 *    report 178), and the SUM-only guardrail already reads it, as `summable`.
 */
const NON_SUM_REPORT_AGGREGATES = new Set(['AVG', 'MIN', 'MAX', 'COUNT'])
export const nonSumReportAggregate = (col) =>
  (NON_SUM_REPORT_AGGREGATES.has(col?.reportFooterAggregateName) ? col.reportFooterAggregateName : null)

// ---- the builder ----------------------------------------------------------------

/**
 * @param {object} report the grid report (`columns`, `reportData`, `reportFooterCells`…)
 * @param {object} state  what the grid knows. Every key is optional so a caller with
 *   no mounted grid (the queue drawer) gets the default view — expanded, unfiltered:
 *   - columns       the grid's (aliased) columns; default `report.columns` that are
 *                   visible and not blank (the grid's own filter)
 *   - order         column ids in the user's order; default the columns' order
 *   - hidden        { columnId: false } for hidden columns
 *   - anyRowFilter  a column filter or a row toggle is active
 *   - rowPasses     row => boolean, the grid's full per-row test
 *   - hideEmptyGroups  the screen hides groups with no match (column filters / aging)
 *   - collapseBase  the list collapse keys are positional in (`rows` or `visibleTop`)
 *   - isCollapsed   key => boolean
 *   - summable      Set of column ids (default: computed like the grid)
 *   - isFiltered, recompute   the on-screen subtotal recompute inputs (outline only)
 *   - forceCurrency the tri-state Show-$ choice
 *   - showThousands spec 212's "Show commas in numbers" (default true)
 *   - footerRows    the report footer block (default: from the report)
 *   - statement     { rolledIds, treeColumnId } in statement mode, else null
 */
export function buildGroupedExportView(report, state = {}) {
  // The grid's columns are CB's VISIBLE, non-blank ones (useAliasedGridColumns);
  // with no grid, the same filter — never the hidden metadata columns.
  const baseColumns = state.columns
    || (report?.columns || []).filter((c) => c.visible !== false && !c.isBlankColumn)
  const rows = report?.reportData || []
  const footerCells = report?.reportFooterCells || []
  const footerRows = state.footerRows || reportFooterLines(report)
  const order = state.order || baseColumns.map((c) => String(c.columnId))
  const hidden = state.hidden || {}
  const isHidden = (col) => hidden[String(col.columnId)] === false
  const anyRowFilter = !!state.anyRowFilter
  const rowPasses = state.rowPasses || (() => true)
  const collapseBase = state.collapseBase || rows
  const isCollapsed = state.isCollapsed || (() => false)
  const summable = state.summable || computeSummable(report, baseColumns)
  const forceCurrency = state.forceCurrency ?? null
  const showThousands = state.showThousands ?? true
  // CC-53905: the same Show $ signal the grid's cells use (currencyEligible).
  const currencyMarked = currencyMarkedColumnIds(report)
  const statement = state.statement || null
  const index = statement ? buildStatementIndex(report, statement.rolledIds) : null
  const tree = index && index.valid ? index : null

  // CC-49872: a numeric server GRAND total marks its column numeric even when CB's
  // metadata hides it (CENTER-aligned + filterType NONE amounts), and rides along
  // per column as the export totals-row fallback.
  const grandNumericIds = numericTotalColumnIds(report)
  // Spec 133: the fallback never overrides a type CB DECLARES non-numeric.
  // CC-53905 review fix 4: this IS `showDollarNumeric` — the grid's cells, totals
  // and Show $ row use the same test, so a saved choice acts on the same columns
  // in this view (and the PDF / client files built from it) as on screen.
  const colIsNumeric = (c) => showDollarNumeric(c, grandNumericIds)
  const serverTotalById = new Map(
    footerCells.map((c) => [String(c.columnId), exportText(String(c.displayValue ?? ''))])
  )
  // Numeric columns export as REAL numbers (CC-49370) so totals stay summable in
  // Excel; text columns export the plain text (spec 133: `exportCell`, which keeps
  // a two-line cell's line break instead of running the lines together).
  const cellsToValues = (cells) => Object.fromEntries(
    baseColumns.map((c) => {
      const cell = cellById(cells, c.columnId)
      return [String(c.columnId), exportCell(cell?.displayValue ?? '', cell?.dataValue, colIsNumeric(c))]
    })
  )
  // CC-49337: the display text a numeric column's export FORMAT (currency symbol,
  // decimals) is read from — the first LEAF whose text is a number, else the first
  // group / report footer line whose text is, else (as it always was) the first
  // leaf with any text. The footer fallback is for an all-zero period: a P&L blanks
  // every $0 account line, so no leaf carries text and the column published
  // currency:false / 0 decimals — the PDF printed Total Income "0" under the
  // screen's "$0.00" and the XLSX got #,##0 (review of spec 127, the live "Today"
  // capture). A number in parentheses is a number ("($530.00)").
  const allLeafRows = collectLeaves(rows)
  const numberText = (cell) => {
    const d = cell?.displayValue
    return d != null && d !== '' && toNumber(String(d).replace(/[()]/g, '')) != null ? d : null
  }
  const formatSample = (id) => {
    for (const lf of allLeafRows) {
      const d = numberText(cellById(lf.dataCells, id))
      if (d != null) return d
    }
    const footerLines = []
    const walkFooters = (rowsArg) => {
      for (const g of rowsArg) {
        if (!g.isGroup) continue
        footerLines.push(...groupFooterLines(g))
        walkFooters(g.dataRows || [])
      }
    }
    walkFooters(rows)
    footerLines.push(...footerRows)
    for (const cells of footerLines) {
      const d = numberText(cellById(cells, id))
      if (d != null) return d
    }
    const anyText = allLeafRows.find((lf) => cellById(lf.dataCells, id)?.displayValue)
    return cellById(anyText?.dataCells, id)?.displayValue
  }
  // What a leaf PRINTS: a rolled statement node prints its rolledCells.
  const printCells = (leaf) => (tree ? tree.displayCells(leaf) : leaf.dataCells)
  const toValues = (leaf) => cellsToValues(leaf.dataCells)
  const toPrintValues = (leaf) => cellsToValues(printCells(leaf))
  const shownByRollup = (leaf) => !tree || !tree.rollupHidden(leaf)
  const allLeaves = collectLeaves(rows)
  const visible = anyRowFilter ? allLeaves.filter(rowPasses) : allLeaves

  // CC-49843: collapse-aware row sets. Keys are positional, so this walk mirrors
  // the renderer exactly: same helper (groupRowKey), same base list, same nesting.
  const collapsedLeaves = new Set()
  const collapsedGroups = new Set()
  const walkCollapse = (rowsArg, level, keyPrefix) => {
    rowsArg.filter((r) => r.isGroup).forEach((g, i) => {
      const key = groupRowKey(keyPrefix, level, i)
      if (isCollapsed(key)) {
        collapsedGroups.add(g)
        collectLeaves(g.dataRows).forEach((lf) => collapsedLeaves.add(lf))
      } else {
        walkCollapse(g.dataRows || [], level + 1, `${key}-`)
      }
    })
  }
  walkCollapse(collapseBase, 0, '')
  const notCollapsed = (lf) => !collapsedLeaves.has(lf)

  const isFiltered = !!state.isFiltered
  const recompute = state.recompute ?? true
  const hideEmptyGroups = !!state.hideEmptyGroups
  const groupHasMatches = (rowsArg) => (rowsArg || []).some((r) => (r.isGroup ? groupHasMatches(r.dataRows) : rowPasses(r)))

  /**
   * The grouped body in the renderer's order (renderRows): a scope's leaves first,
   * in CB order, then its sub-groups — heading, body, subtotal lines.
   *   { type: 'heading',  level, text }                 omitted when the text is empty
   *   { type: 'row',      level, depth, kind, values }   kind: 'account' | 'total' | 'rolled' | undefined
   *   { type: 'subtotal', level, line, lines, values }   group footer line `line` of `lines`
   * `honorFilters` = the screen's filters/toggles apply (`outline`); off, every row
   * (`outlineAll`). Roll-up and collapse apply to both — they are presentation,
   * not filters. A collapsed group emits its heading and subtotal lines only.
   * Subtotal line 0 in `outline` carries exactly what the screen shows (the
   * SUM-safe recompute, same helper); `outlineAll` carries CB's values verbatim.
   */
  const buildOutline = (honorFilters) => {
    const out = []
    const rowEntry = (leaf, level) => {
      const t = tree ? tree.treeOf(leaf) : null
      const rolled = !!t && tree.isRolled(leaf)
      const entry = { type: 'row', level, depth: t ? (Number(t.depth) || 0) : 0 }
      if (t) entry.kind = t.kind === 'close' ? 'total' : (rolled ? 'rolled' : 'account')
      entry.values = toPrintValues(leaf)
      return entry
    }
    const subtotalValues = (g, cells, line) => {
      const values = cellsToValues(cells)
      if (!honorFilters || line !== 0) return values
      const groupAll = collectLeaves(g.dataRows)
      const groupVisible = isFiltered ? groupAll.filter(rowPasses) : groupAll
      for (const c of baseColumns) {
        const id = String(c.columnId)
        const { safe, sum } = footerRecompute({
          colId: id,
          serverDisplay: cellById(cells, c.columnId)?.displayValue ?? '',
          visibleLeaves: groupVisible,
          allLeaves: groupAll,
          isFiltered,
          recompute,
          summable,
        })
        if (safe) values[id] = sum
      }
      return values
    }
    const walk = (rowsArg, level) => {
      let leaves = rowsArg.filter((r) => !r.isGroup)
      if (honorFilters && anyRowFilter) leaves = leaves.filter(rowPasses)
      leaves = leaves.filter(shownByRollup)
      for (const lf of leaves) out.push(rowEntry(lf, level))
      for (const g of rowsArg.filter((r) => r.isGroup)) {
        if (honorFilters && hideEmptyGroups && !groupHasMatches(g.dataRows)) continue
        const text = groupHeadingText(g.groupHeaderContent).trim()
        if (text) out.push({ type: 'heading', level, text })
        if (!collapsedGroups.has(g)) walk(g.dataRows || [], level + 1)
        const lines = groupFooterLines(g)
        lines.forEach((cells, line) => {
          out.push({ type: 'subtotal', level, line, lines: lines.length, values: subtotalValues(g, cells, line) })
        })
      }
    }
    walk(rows, 0)
    return out
  }

  return {
    // CC-49843: top-level group structure for the PDF "summary only" scope — one
    // entry per group: header text + the SERVER subtotal row (verbatim values,
    // never recomputed). `collapsed` lets a consumer render a collapsed section as
    // its header + subtotal alone. Always false in statement mode (S1).
    groups: rows.filter((r) => r.isGroup).map((g) => ({
      header: groupHeadingText(g.groupHeaderContent),
      footer: g.groupFooterCells?.length ? cellsToValues(g.groupFooterCells) : null,
      // CC-50050: the whole subtotal block; `footer` stays line 0.
      footerRows: groupFooterLines(g).map(cellsToValues),
      collapsed: collapsedGroups.has(g),
    })),
    // CC-49843: visibleRows/allRows minus the leaves inside collapsed groups — and,
    // in statement mode, minus rows a rolled parent hides, with a rolled parent's
    // CB-rolled figures (spec 127 S5: print what the screen shows). Separate fields
    // so the CSV/Excel writers, which are dataset exports, keep their behaviour.
    printRows: visible.filter(notCollapsed).filter(shownByRollup).map(toPrintValues),
    printRowsAll: allLeaves.filter(notCollapsed).filter(shownByRollup).map(toPrintValues),
    // `summable` = safe to total (CC-49457), the SUM-only guardrail (CC-49382).
    columns: baseColumns.map((c, idx) => {
      const id = String(c.columnId)
      const pos = order.indexOf(id)
      // CC-49337: sample-derived display format for real Excel number formats.
      const numeric = colIsNumeric(c)
      const fmt = numeric ? detectNumberFormat(formatSample(id)) : null
      // spec 127 §4.1: `percent` is DECLARED only — never detected. A declared
      // percentage takes its declared `decimals` and is never currency: the sample
      // above is the first cell with text, which on a Comparative P&L can be "—"
      // (a zero prior) — decimals 0 printed "-77%" beside the screen's "-76.5%",
      // and "Show $" then flagged it currency with 2 places (review of spec 127).
      const percent = !!c.percent
      let currency
      let decimals
      if (percent) {
        currency = false
        decimals = Number.isFinite(c.decimals) ? c.decimals : (fmt?.decimals ?? 0)
      } else {
        // CC-53905: Show $ grants currency only to a column the screen puts a "$"
        // on (currencyEligible — CB's CellsFormat 'C', or an adapter column its
        // source marks; spec 133's neverCurrency columns stay excluded), and never
        // forces 2 decimals: a C0 column keeps the display's own 0.
        const forced = forceCurrency === true && numeric && !fmt?.percent && currencyEligible(c, currencyMarked)
        currency = forceCurrency === false ? false : ((fmt?.currency ?? false) || forced)
        // Spec 209: a column that DECLARES an integer `decimals` (an Advanced Report's
        // number column, from cb-api's reader) keeps it; the sample is the first record,
        // and "2" above "1.25" printed 1 and 3 in every file. No other grouped column
        // declares one (swept 2026-10-30), so their precision is still the sample's.
        decimals = Number.isInteger(c.decimals) ? c.decimals : (fmt?.decimals ?? 0)
      }
      const aggregate = nonSumReportAggregate(c)
      // Spec 211: a column the screen groups but this view writes as text (a
      // left-aligned DECIMAL) — the PDF groups its text too; CSV / Excel ignore it.
      const thousands = !numeric && groupsThousands(c, grandNumericIds)
      return {
        key: id,
        label: c.label,
        visible: !isHidden(c),
        order: pos === -1 ? 999 + idx : pos,
        numeric,
        summable: summable.has(id),
        serverTotal: serverTotalById.get(id) ?? '',
        currency,
        decimals,
        percent,
        // Spec 153: present only on a declared AVG / MIN / MAX / COUNT column.
        ...(aggregate ? { aggregate } : {}),
        ...(thousands ? { thousands } : {}),
        // Spec 212: off, the PDF and client XLSX / CSV print no separators, as the screen.
        ...(showThousands ? {} : { grouping: false }),
      }
    }),
    visibleRows: visible.map(toValues), // filtered leaves (flattened), raw figures
    allRows: allLeaves.map(toValues),
    // CC-49860: the FULL footer block, verbatim server values (only row 0 is
    // validated against the SUM-only guardrail downstream).
    footerRows: footerRows.map(cellsToValues),
    // CC-50004: the tri-state "Show $ on amounts" choice.
    // CC-53905: only where the grid offers the toggle (`currencyToggleActs`) — a
    // choice stored on a report it cannot act on must not move the export off
    // the server build.
    currencyOverride: currencyToggleActs(baseColumns, currencyMarked, grandNumericIds) ? (forceCurrency ?? null) : null,
    // Spec 153: does the grand-total row SHOW a re-add of the visible leaves? It is
    // footerRecompute's own condition (a filter or row toggle is on, even one that
    // hides nothing, and "Total just the filtered rows" is checked), so the AI can
    // quote a re-added total exactly as the screen prints it (aiPayload.js).
    totalsRecomputed: isFiltered && !!recompute,
    outline: buildOutline(true),
    outlineAll: buildOutline(false),
  }
}

export default buildGroupedExportView
